import { Swiper, SwiperSlide } from 'swiper/react';
import { Grid, Pagination, Navigation } from 'swiper/modules';
import { Discount, MoneyBag, OrderDocument, PromoTag } from 'components/customIcons';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/outline';
import { DataCard } from '../dataCard';
import { DatePickerFilterOption, IOverviewData } from 'interfaces';
import { get } from 'lodash';
import { useOne } from '@refinedev/core';
import moment from 'moment';
import { currencySymbol } from 'utills/helpers';

const CARD_DATA = [
  {
    name: "Gross revenue",
    unit: true,
    hint: "The total value of successfully paid orders for all events (excluding deductions, discounts, or other costs) within the selected timeframe. The data will be calculated based on the time when the successful orders were recorded.",
    dataId: "total_subtotal",
    showGrowthRates: true,
    icon: {
      svg: MoneyBag,
      fill: "#58BF5E",
      background: "#DDFDCD",
    }
  },
  {
    name: "Orders",
    unit: false,
    hint: "The total number of successfully paid orders for all events within the selected timeframe. The data will be calculated based on the time when the successful orders were recorded.",
    dataId: "total_orders",
    showGrowthRates: true,
    icon: {
      svg: OrderDocument,
      fill: "#FF82AC",
      background: "#FFE0EB",
    },
    redirect: "/orders"
  },
  {
    name: "Items",
    unit: false,
    hint: "The total number of tickets belonging to all successfully paid orders for all events within the selected timeframe. The data will be calculated based on the time when the successful orders were recorded.",
    dataId: "total_total_item",
    showGrowthRates: true,
    icon: {
      svg: PromoTag,
      fill: "#396AFF",
      background: "#E7EDFF",
    }
  },
  {
    name: "Net revenue",
    unit: true,
    hint: "The total value of all successfully paid orders, after deducting discounts on ActiUp and from the payment gateway, for all events within the selected timeframe. The data will be calculated based on the time when the successful orders were recorded.",
    dataId: "total_grand_total",
    showGrowthRates: true,
    icon: {
      svg: MoneyBag,
      fill: "#1B9DA0",
      background: "#E2F5F5",
    }
  },
  {
    name: "Discount",
    unit: true,
    hint: "The total discount value on ActiUp for all successfully paid orders of all events within the selected timeframe. The data will be calculated based on the time when the successful orders were recorded.",
    dataId: "total_discount_coupon",
    showGrowthRates: false,
    icon: {
      svg: Discount,
      fill: "#F7A06E",
      background: "#FFF5D9",
    }
  },
  {
    name: "Promotion",
    unit: true,
    hint: "The total discount value from the payment gateway for all successfully paid orders of all events within the selected timeframe. The data will be calculated based on the time when the successful orders were recorded.",
    dataId: "total_discount_promotion",
    showGrowthRates: false,
    icon: {
      svg: Discount,
      fill: "#F87171",
      background: "#FFF5D9",
    }
  }
]

const SLIDER_BREAKPOINT: any = {
  640: {
    slidesPerView: 2,
    spaceBetween: 12,
    slidesPerGroup: 2,
    allowTouchMove: false,
  },
  768: {
    slidesPerView: 3,
    spaceBetween: 16,
    slidesPerGroup: 3,
    allowTouchMove: false,
    grid: {
      rows: 1,
      fill: "row"
    }
  },
  1024: {
    slidesPerView: 4,
    spaceBetween: 24,
    slidesPerGroup: 4,
    allowTouchMove: false,
    grid: {
      rows: 1,
      fill: "row"
    }
  },
};

// interface TotalDataSliderProps {
//   overviewData: IOverviewData | null;
// }

export const TotalDataSlider = ({currentFilterValues, dateOption} : {currentFilterValues: any, dateOption: DatePickerFilterOption }) => {

  const { data } = useOne<IOverviewData>({
    resource: "dashboard/overview",
    id: "",
    meta: {
      resource: "data",
      filters: [
        {
          field: "organization_id",
          operator: "eq",
          value: currentFilterValues?.organization_id ?? "",
        },
        {
          field: "from_date",
          operator: "eq",
          value: currentFilterValues?.from_date ?? ""
        },
        {
          field: "to_date",
          operator: "eq",
          value: currentFilterValues?.to_date ?? ""
        },
        {
          field: "ticket_platform",
          operator: "eq",
          value: currentFilterValues?.ticket_platform ?? ""
        },
        {
          field: "gateway_owner",
          operator: "eq",
          value: currentFilterValues?.gateway_owner ?? ""
        },
        {
          field: "gateway_partner_code",
          operator: "eq",
          value: currentFilterValues?.gateway_partner_code ?? ""
        },
        {
          field: "gateway_name",
          operator: "eq",
          value: currentFilterValues?.gateway_name ?? ""
        },
        {
          field: "currency",
          operator: "eq",
          value: localStorage.getItem("currency") ?? ""
        },
      ],
    },
    queryOptions: {
      enabled: !!currentFilterValues,
    } as any,
  });

  const overviewData = data?.data ?? null;

  return (
    <div className="relative">
      <Swiper
        slidesPerView={2}
        spaceBetween={12}
        slidesPerGroup={2}
        className="!pb-10 sm:!pb-0"
        grid={{
          rows: 2,
          fill: "row"
        }}
        navigation={{
          nextEl: ".next-arrow",
          prevEl: ".prev-arrow",
          disabledClass: "opacity-0"
        }}
        pagination={{
          el: "#bulletsContainer",
          type: "bullets",
          clickable: true,
          renderBullet: function (index: number, className: string) {
            return '<span class="' + className + '"></span>';
          },
          bulletClass: "sm:hidden !rounded-md !w-10 !h-2 bg-magnolia-400",
          bulletActiveClass: "!bg-white",
        }}
        breakpoints={SLIDER_BREAKPOINT}
        modules={[Navigation, Grid, Pagination]}
      >
        {CARD_DATA.map((data, index) => (
          <SwiperSlide key={data.dataId} className="!min-h-[195px] !h-auto sm:!min-h-min">
            <DataCard
              name={data.name}
              hint={data.hint}
              icon={data.icon}
              unit={data.unit ? currencySymbol() : ""}
              value={get(overviewData?.stats, `${data.dataId}`, null)}
              change={get(overviewData?.growth_rates, `${data.dataId}`, 0)}
              showGrowthRates={data.showGrowthRates}
              redirectUrl={data?.redirect}
              dateOption={dateOption}
              dayDiff={moment(currentFilterValues.to_date).diff(moment(currentFilterValues.from_date), "days")+1}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {/* Navigation */}
      <button className="prev-arrow hidden sm:flex justify-center items-center absolute top-1/2 -left-4 -translate-y-1/2 z-[3] w-8 h-8 bg-white border border-magnolia-700 rounded-full">
        <ArrowLeftIcon className="w-4 h-4 text-brand-500" />
      </button>
      <button className="next-arrow hidden sm:flex justify-center items-center absolute top-1/2 -right-4 -translate-y-1/2 z-[3] w-8 h-8 bg-white border border-magnolia-700 rounded-full">
        <ArrowRightIcon className="w-4 h-4 text-brand-500" />
      </button>
      {/* Dots */}
      <div id="bulletsContainer" className="w-full absolute bottom-0 flex justify-center gap-x-3"></div>
    </div>
  )
}